import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingCart, LogOut, Loader2, ShieldAlert, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AdminOverview from '@/pages/admin/AdminOverview';
import AdminProducts from '@/pages/admin/AdminProducts';
import AdminOrders from '@/pages/admin/AdminOrders';
import { Logo } from '@/components/motion/Primitives';
import { getErrorMessage } from '@/lib/format';

const TABS = [
    { key: 'overview', label: 'Overview', icon: LayoutDashboard },
    { key: 'products', label: 'Products', icon: Package },
    { key: 'orders', label: 'Orders', icon: ShoppingCart },
];

function AdminLogin() {
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(email, password);
            if (user.role !== 'admin') toast.error('This account is not an admin.');
            else toast.success('Welcome, admin!');
        } catch (err) {
            toast.error(getErrorMessage(err, 'Login failed.'));
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen grid place-items-center px-4 royal-panel">
            <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl">
                <div className="flex justify-center"><Logo className="h-9" /></div>
                <h1 className="font-display text-2xl font-bold text-center mt-4">Admin Dashboard</h1>
                <p className="text-center text-sm text-[var(--ink-muted)] mt-1">Sign in to manage your store</p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div><Label htmlFor="aemail">Email</Label><Input id="aemail" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" placeholder="admin@xtra.com" /></div>
                    <div><Label htmlFor="apass">Password</Label><Input id="apass" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" placeholder="••••••••" /></div>
                    <button data-testid="admin-login-button" disabled={loading} className="btn-blue w-full h-12 rounded-xl font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-80">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Sign in'}</button>
                </form>
                <Link to="/" className="mt-5 flex items-center justify-center gap-1.5 text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]"><ArrowLeft className="h-4 w-4" /> Back to store</Link>
            </div>
        </div>
    );
}

export default function AdminDashboard() {
    const { user, loading, isAdmin, logout } = useAuth();
    const [tab, setTab] = useState('overview');

    if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[var(--brand-blue)]" /></div>;
    if (!user) return <AdminLogin />;
    if (!isAdmin) return (
        <div className="min-h-screen grid place-items-center px-4 text-center">
            <div><ShieldAlert className="h-12 w-12 text-red-500 mx-auto" /><h1 className="font-display text-2xl font-bold mt-4">Access denied</h1><p className="text-[var(--ink-muted)] mt-2">You need an admin account to view this page.</p><button onClick={logout} className="btn-blue mt-6 h-11 px-6 rounded-xl font-semibold">Log out</button></div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[var(--surface-2)]">
            <div className="flex">
                {/* Sidebar */}
                <aside className="hidden lg:flex w-64 shrink-0 flex-col royal-panel min-h-screen sticky top-0">
                    <div className="px-5 py-5 flex items-center gap-3 border-b border-white/10">
                        <Logo variant="dark" className="h-6" />
                        <span className="text-white/50 text-[11px] font-bold uppercase tracking-wider pl-3 border-l border-white/15">Admin</span>
                    </div>
                    <nav className="flex-1 p-3 space-y-1">
                        {TABS.map((t) => (
                            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === t.key ? 'bg-white text-[var(--brand-royal)]' : 'text-white/80 hover:bg-white/10'}`}>
                                <t.icon className="h-5 w-5" /> {t.label}
                            </button>
                        ))}
                    </nav>
                    <div className="p-3 border-t border-white/10 space-y-1">
                        <Link to="/" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/80 hover:bg-white/10"><ArrowLeft className="h-4 w-4" /> View store</Link>
                        <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/80 hover:bg-white/10"><LogOut className="h-4 w-4" /> Log out</button>
                    </div>
                </aside>

                {/* Main */}
                <main className="flex-1 min-w-0">
                    {/* Mobile tab bar */}
                    <div className="lg:hidden royal-panel px-4 py-3 flex items-center gap-2 overflow-auto no-scrollbar sticky top-0 z-10">
                        <Logo variant="dark" className="h-5 mr-1" />
                        {TABS.map((t) => (
                            <button key={t.key} onClick={() => setTab(t.key)} className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${tab === t.key ? 'bg-white text-[var(--brand-royal)]' : 'text-white/80'}`}>{t.label}</button>
                        ))}
                        <button onClick={logout} className="ml-auto text-white/80"><LogOut className="h-5 w-5" /></button>
                    </div>

                    <div className="p-5 lg:p-8">
                        {tab === 'overview' && <AdminOverview />}
                        {tab === 'products' && <AdminProducts />}
                        {tab === 'orders' && <AdminOrders />}
                    </div>
                </main>
            </div>
        </div>
    );
}
